import React from "react";
import { Link } from "react-router-dom";
import { Users, Camera, Zap, Timer, Target, ChevronRight, PhoneCall } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider } from "../components/ValuationDialog";
import Seo from "../components/Seo";
import { VALUATION_TYPES } from "../components/valuation/ValuationShell";
import { CONTACT } from "../mock";

const ICONS = { "in-person": Users, virtual: Camera, instant: Zap };

function OptionRow({ t }) {
  const Icon = ICONS[t.key];
  return (
    <Link to={t.path} data-testid={`valuation-option-${t.key}`}
      className="group grid items-center gap-5 rounded-2xl border border-[#e5ddd0] bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-[#35a4a4] hover:shadow-lg sm:grid-cols-[1fr_auto_auto] sm:gap-8">
      <span className="inline-flex items-center gap-3 rounded-full bg-[#16323f] px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors group-hover:bg-[#ec008c]">
        <Icon size={16} className="text-[#d8b074] group-hover:text-white" /> {t.name}
      </span>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#7a716a]">Duration</p>
        <p className="mt-0.5 text-sm text-[#16323f]">{t.duration}</p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#7a716a]">Accuracy</p>
        <p className="mt-0.5 text-sm text-[#16323f]">{t.accuracy}</p>
      </div>
    </Link>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-4 rounded-xl border border-[#e5ddd0] bg-white p-4">
      <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#16323f] text-[#d8b074]"><Icon size={22} strokeWidth={1.8} /></span>
      <div>
        <p className="font-display text-base font-600 text-[#16323f]">{label}</p>
        <p className="text-sm text-[#5b5450]">{value}</p>
      </div>
    </div>
  );
}

function Detail({ t, flip }) {
  return (
    <div id={t.key} className="scroll-mt-28 grid items-center gap-10 lg:grid-cols-2 lg:gap-16" data-testid={`valuation-detail-${t.key}`}>
      <div className={`relative overflow-hidden rounded-2xl ${flip ? "lg:order-2" : ""}`}>
        <img src={t.image} alt={t.name} className="h-[420px] w-full object-cover lg:h-[520px]" loading="lazy" />
        <div className="absolute inset-0 bg-[#16323f]/55" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 p-6 text-center">
          <p className="font-display text-2xl font-600 text-white lg:text-3xl">{t.name}</p>
          <Link to={t.path} data-testid={`lets-begin-${t.key}`}
            className="group inline-flex items-center gap-2 rounded-full bg-[#ec008c] px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]">
            Let&rsquo;s begin <ChevronRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
      <div className={flip ? "lg:order-1" : ""}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">{t.tagline}</p>
        <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-wide text-[#16323f] lg:text-4xl">
          The {t.name} is for you if&hellip;
        </h2>
        <span className="mt-4 block h-[3px] w-16 rounded-full bg-[#eaa32a]" />
        {t.paras.map((p, i) => <p key={i} className="mt-5 text-[15px] leading-relaxed text-[#5b5450]">{p}</p>)}
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <Stat icon={Timer} label="Duration" value={t.duration} />
          <Stat icon={Target} label="Accuracy" value={t.accuracy} />
        </div>
      </div>
    </div>
  );
}

function Content() {
  return (
    <div className="App">
      <Seo title="Free Rental Valuation | New Space Lettings, Hackney" description="Three ways to find out what your property could let for: an in-person valuation by Richard or George, a virtual valuation from your photos, or an instant online estimate." path="/valuation" />
      <Header />

      <section className="relative overflow-hidden bg-[#16323f] pt-32 pb-16 lg:pt-44 lg:pb-24" data-testid="valuation-hero">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#35a4a4]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-[#d8b074]/10 blur-3xl" />
        <div className="relative mx-auto grid max-w-[1200px] items-center gap-12 px-5 lg:grid-cols-[5fr_6fr] lg:gap-16">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#d8b074]">Free Rental Valuation</p>
            <h1 className="mt-4 font-display text-4xl font-700 uppercase leading-tight tracking-wide text-white lg:text-6xl">
              How much could your property let for?
            </h1>
            <p className="mt-6 text-[15px] leading-relaxed text-white/80">We like to give landlords options — so there are three ways to get a figure.</p>
            <p className="mt-4 text-[15px] leading-relaxed text-white/80">
              Get a super-fast computer-generated estimate, send us photos for a virtual valuation, or book Richard or George to visit in person with a detailed pricing plan.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-white/80">Your choice — and you&rsquo;re always welcome to just call us.</p>
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="valuation-call-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#d8b074] transition-colors hover:text-white">
              <PhoneCall size={16} /> {CONTACT.phone}
            </a>
          </div>
          <div className="space-y-4" data-testid="valuation-options">
            {VALUATION_TYPES.map((t) => <OptionRow key={t.key} t={t} />)}
          </div>
        </div>
      </section>

      <section className="bg-[#faf7f0] py-16 lg:py-24">
        <div className="mx-auto max-w-[1200px] space-y-20 px-5 lg:space-y-28">
          {VALUATION_TYPES.map((t, i) => <Detail key={t.key} t={t} flip={i % 2 === 1} />)}
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
}

export default function Valuation() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
